import { getCubeMeta } from '@/services/api.js'

import {
  getCubeName,
  getCubeVersion,
  getIndicatorName,
  getAllDimensions,
  getTimeDimension,
  getIndicatorDimension,
  getNonIndicatorDimensions,
  getCountryDimension,
  getCubeTimeRange,
  extendCubeWithConfig,
  rewriteCubeDimensionsTypes,
  applyCubeConstraints,
  getUnitDimension,
  getTimeScaleDimension,
} from '@/utils/cube.js'

const openSockets = []

export default {
  state: {
    main: null,
    pair: null,
    size: null,
    mainDataIndicators: [],
    pairDataIndicators: [],
    sizeDataIndicators: [],
    mainBreakdownDimensions: [],
    pairBreakdownDimensions: [],
    sizeBreakdownDimensions: [],
  },
  getters: {
    mainCubeVersion(state) {
      return state.main ? getCubeVersion(state.main) : null
    },
    pairCubeVersion(state) {
      return state.pair ? getCubeVersion(state.pair) : null
    },
    sizeCubeVersion(state) {
      return state.size ? getCubeVersion(state.size) : null
    },
    mainCubeName(state) {
      return state.main ? getCubeName(state.main) : null
    },
    pairCubeName(state) {
      return state.pair ? getCubeName(state.pair) : null
    },
    sizeCubeName(state) {
      return state.size ? getCubeName(state.size) : null
    },
    indicatorName: state => (slot, indId, lng) => {
      return state[slot] ? getIndicatorName(state[slot], indId, lng) : null
    },
    mainDimensions(state) {
      return state.main ? getAllDimensions(state.main) : []
    },
    pairDimensions(state) {
      return state.pair ? getAllDimensions(state.pair) : []
    },
    sizeDimensions(state) {
      return state.size ? getAllDimensions(state.size) : []
    },
    mainTimeDimension(state) {
      return state.main ? getTimeDimension(state.main) : null
    },
    pairTimeDimension(state) {
      return state.pair ? getTimeDimension(state.pair) : null
    },
    sizeTimeDimension(state) {
      return state.size ? getTimeDimension(state.size) : null
    },
    mainIndicatorDimension(state) {
      return state.main ? getIndicatorDimension(state.main) : null
    },
    pairIndicatorDimension(state) {
      return state.pair ? getIndicatorDimension(state.pair) : null
    },
    sizeIndicatorDimension(state) {
      return state.size ? getIndicatorDimension(state.size) : null
    },
    mainNonIndicatorDimensions(state) {
      return state.main ? getNonIndicatorDimensions(state.main) : []
    },
    pairNonIndicatorDimensions(state) {
      return state.pair ? getNonIndicatorDimensions(state.pair) : []
    },
    sizeNonIndicatorDimensions(state) {
      return state.size ? getNonIndicatorDimensions(state.size) : []
    },
    mainCountryDimension(state) {
      return state.main ? getCountryDimension(state.main) : null
    },
    pairCountryDimension(state) {
      return state.pair ? getCountryDimension(state.pair) : null
    },
    sizeCountryDimension(state) {
      return state.size ? getCountryDimension(state.size) : null
    },
    mainUnitDimension(state) {
      return state.main ? getUnitDimension(state.main) : null
    },
    pairUnitDimension(state) {
      return state.pair ? getUnitDimension(state.pair) : null
    },
    sizeUnitDimension(state) {
      return state.size ? getUnitDimension(state.size) : null
    },
    mainTimeScaleDimension(state) {
      return state.main ? getTimeScaleDimension(state.main) : null
    },
    pairTimeScaleDimension(state) {
      return state.pair ? getTimeScaleDimension(state.pair) : null
    },
    sizeTimeScaleDimension(state) {
      return state.size ? getTimeScaleDimension(state.size) : null
    },
    mainAvailableGroups(state) {
      return state.main ? getIndicatorDimension(state.main).options.filter(d => d.children) : []
    },
    pairAvailableGroups(state) {
      return state.pair ? getIndicatorDimension(state.pair).options.filter(d => d.children) : []
    },
    sizeAvailableGroups(state) {
      return state.size ? getIndicatorDimension(state.size).options.filter(d => d.children) : []
    },
    mainAvailableIndicators(state) {
      return state.main
        ? getIndicatorDimension(state.main).options.reduce(
            (inds, ind) => inds.concat(ind.children),
            []
          )
        : []
    },
    pairAvailableIndicators(state) {
      return state.pair
        ? getIndicatorDimension(state.pair).options.reduce(
            (inds, ind) => inds.concat(ind.children),
            []
          )
        : []
    },
    sizeAvailableIndicators(state) {
      return state.size
        ? getIndicatorDimension(state.size).options.reduce(
            (inds, ind) => inds.concat(ind.children),
            []
          )
        : []
    },
    mainCubeTimeRange(state) {
      return state.main ? getCubeTimeRange(state.main) : []
    },
    pairCubeTimeRange(state) {
      return state.pair ? getCubeTimeRange(state.pair) : []
    },
    sizeCubeTimeRange(state) {
      return state.size ? getCubeTimeRange(state.size) : []
    },
    breakdownDefaults(state) {
      return ['main', 'pair', 'size'].reduce((defaults, slot) => {
        const key = slot + 'Breakdowns'
        const dimensions = state[slot] ? state[slot + 'BreakdownDimensions'] : [] // getBreakdownDimensions(state[slot]) : []
        if (dimensions.length) defaults[key] = {}
        dimensions.forEach(dim => {
          defaults[key][dim.id] = dim.options.find(opt => opt.isDefault)?.id || dim.options[0].id
        })
        return defaults
      }, {})
    },
  },
  mutations: {
    mainCubeMeta(state, data) {
      state.main = data
    },
    pairCubeMeta(state, data) {
      state.pair = data
    },
    sizeCubeMeta(state, data) {
      state.size = data
    },
    mainBreakdownDimensions(state, data) {
      state.mainBreakdownDimensions = data
    },
    pairBreakdownDimensions(state, data) {
      state.pairBreakdownDimensions = data
    },
    sizeBreakdownDimensions(state, data) {
      state.sizeBreakdownDimensions = data
    },
    rewriteCubeDimensionsTypes(state, params) {
      state[params.slot] = rewriteCubeDimensionsTypes(state[params.slot], params.changes)
    },
    wipeMeta(state) {
      state.main = null
      state.pair = null
      state.size = null
      state.mainDataIndicators = []
      state.pairDataIndicators = []
      state.sizeDataIndicators = []
      state.mainBreakdownDimensions = []
      state.pairBreakdownDimensions = []
      state.sizeBreakdownDimensions = []
    },
  },
  actions: {
    getCubeMeta(store, { cubeId, sourceSlots }) {
      const config = store.rootState.external.versionInfo.cubes.find(cube => cube.metaId === cubeId)
      const flip = store.rootState.query.flip
      openSockets.push(true)
      return getCubeMeta({ cubeId, cubeSource: config.cubeSource }).then(res => {
        openSockets.pop()
        if (openSockets.length < sourceSlots) {
          const meta = applyCubeConstraints(extendCubeWithConfig(res, { config, flip }))
          return meta
        }
        throw new Error('Discarded meta request')
      })
    },
  },
}
